import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { ICountryRate, useDataValidationRates } from './use-data-validation-rates';
import './rate-drawer.scss';

type ISort = 'az' | 'lo' | 'hi';

interface IRateDrawer {
	open: boolean;
	onClose: () => void;
	/** The country the tile quotes — pinned to the top and marked. */
	country?: string | null;
	priceNote?: string;
}

const fmt = (n: number) => `$${Number(n).toFixed(2)}`;

/**
 * The service line under a country name. The rate card names the country inside
 * the service string ("Austria Credit Bureau", "Australia — default not
 * established"), which reads as a stutter directly under the country's own
 * name, so the prefix comes off. A country running several services is summed
 * into one price, so it is described by the count rather than by a first name
 * that would misrepresent what gets billed.
 */
const serviceLine = (row: ICountryRate): string => {
	if (row.services.length > 1) return `${row.services.length} services`;
	const raw = row.services[0]?.service ?? '';
	return raw.replace(new RegExp(`^${row.country}\\s*[-—–]\\s*`), '').trim() || '—';
};

const coverageOf = (row: ICountryRate): string => {
	const cov = row.services.map(s => s.coverage).find(c => !!c);
	return cov || '—';
};

/**
 * DESIGN 3 — the rate drawer.
 *
 * PORTALED TO <body>, AND IT HAS TO BE. The drawer is `position: fixed`, and
 * react-flow puts a `transform` on its viewport: a transformed ancestor makes a
 * fixed child position against THAT box instead of the window, so a drawer
 * rendered inside the node would be laid out against the panned, zoomed canvas
 * and would scale and slide with it. Outside the viewport it also survives the
 * node being dragged, and the canvas stays live to the left of it.
 *
 * The portal root carries `sv2-scope sv2` so the shared drawer/table primitives
 * in surface.scss apply and the tokens resolve — <body> is a sibling of the
 * builder's own token scope, not a descendant of it.
 */
export const RateDrawer: FC<IRateDrawer> = ({
	open,
	onClose,
	country,
	priceNote,
}) => {
	const { sheet, loading } = useDataValidationRates(open);
	const [query, setQuery] = useState('');
	const [sort, setSort] = useState<ISort>('az');

	/* Every open starts from the same place — a search left over from last time
	   reads as an empty rate card. */
	useEffect(() => {
		if (open) {
			setQuery('');
			setSort('az');
		}
	}, [open]);

	/*
	 * Esc, and click-away — NOT a scrim. A transparent overlay would close on a
	 * canvas click too, but it would also swallow that click: panning, dragging a
	 * node and ticking another box would all go dead while the sheet is open,
	 * which is the opposite of reading a price without leaving the page. A
	 * listener leaves the canvas live.
	 */
	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		const onDown = (e: MouseEvent) => {
			const el = e.target as HTMLElement | null;
			if (!el) return;
			/* The chip is the toggle; letting this fire for it would close the
			   drawer in the same gesture that opened it. */
			if (el.closest('.dv-rates__drawer') || el.closest('.kyc-action__rate-chip')) {
				return;
			}
			onClose();
		};
		/* CAPTURE phase, and it has to be: react-flow pans the canvas with
		   d3-zoom, whose own mousedown handler calls stopImmediatePropagation().
		   A bubble-phase listener on document therefore never hears a click on
		   the canvas — the drawer stayed open behind whatever was clicked, which
		   is the one close path a user is most likely to reach for. */
		document.addEventListener('keydown', onKey);
		document.addEventListener('mousedown', onDown, true);
		return () => {
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('mousedown', onDown, true);
		};
	}, [open, onClose]);

	const rows = useMemo(() => {
		const all = sheet?.countries ?? [];
		const q = query.trim().toLowerCase();
		const matched = q
			? all.filter(
					r =>
						r.country.toLowerCase().includes(q) ||
						r.services.some(s => (s.service ?? '').toLowerCase().includes(q))
			  )
			: all.slice();

		return matched.sort((a, b) => {
			/* The flow's own country pins first in every sort — it is the number
			   the tile is quoting, so it should never need finding. */
			const am = a.country === country ? 1 : 0;
			const bm = b.country === country ? 1 : 0;
			if (bm - am) return bm - am;
			if (sort === 'lo') return a.total - b.total || a.country.localeCompare(b.country);
			if (sort === 'hi') return b.total - a.total || a.country.localeCompare(b.country);
			return a.country.localeCompare(b.country);
		});
	}, [sheet, query, sort, country]);

	const range = useMemo(() => {
		const all = sheet?.countries ?? [];
		if (!all.length) return null;
		return {
			from: Math.min(...all.map(c => c.from)),
			to: Math.max(...all.map(c => c.total)),
		};
	}, [sheet]);

	const stop = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

	const fetched = sheet?.fetchedAt
		? new Date(sheet.fetchedAt).toISOString().slice(0, 10)
		: null;

	return createPortal(
		<div className="sv2-scope sv2 dv-rates" onClick={stop} role="presentation">
			<aside
				className={`drawer dv-rates__drawer${open ? ' open' : ''}`}
				aria-label="Data validation rates by country"
				aria-hidden={!open}
			>
				<div className="dhead">
					<div className="dt">
						<h2>Data Validation — rates by country</h2>
						<button
							className="dx"
							type="button"
							onClick={onClose}
							aria-label="Close rates"
						>
							&#10005;
						</button>
					</div>
					<div className="dv-rates__note">
						{range && (
							<b>
								{fmt(range.from)}–{fmt(range.to)} per verification, depending on
								country.{' '}
							</b>
						)}
						{priceNote}
					</div>
				</div>

				<div className="dv-rates__ctl">
					<div className="srch">
						<svg viewBox="0 0 24 24" aria-hidden="true">
							<circle cx="11" cy="11" r="7" />
							<line x1="16.5" y1="16.5" x2="21" y2="21" />
						</svg>
						<input
							type="text"
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder={`Search ${sheet?.countries?.length ?? ''} countries…`}
							aria-label="Search countries"
						/>
					</div>
					{(
						[
							['az', 'A–Z'],
							['lo', '$ low'],
							['hi', '$ high'],
						] as [ISort, string][]
					).map(([key, label]) => (
						<button
							key={key}
							type="button"
							className={`pre${sort === key ? ' on' : ''}`}
							onClick={() => setSort(key)}
						>
							{label}
						</button>
					))}
				</div>

				{loading && !sheet ? (
					<div className="dv-rates__empty">Loading rates…</div>
				) : !sheet ? (
					<div className="dv-rates__empty">
						Rates are unavailable right now. The price on the step is the one
						that bills.
					</div>
				) : (
					<>
						<table className="stbl dv-rates__table">
							<thead>
								<tr>
									<th>Country / registry check</th>
									<th>Coverage</th>
									<th className="num">Price</th>
								</tr>
							</thead>
							<tbody>
								{rows.map(r => (
									<tr
										key={r.country}
										className={r.country === country ? 'is-mine' : ''}
									>
										<td>
											<span className="nm">
												{r.country}
												{r.country === country && (
													<span className="dv-rates__tag">your flow</span>
												)}
											</span>
											<div className="svc">{serviceLine(r)}</div>
										</td>
										<td className="cov">{coverageOf(r)}</td>
										<td className="num pr">{fmt(r.total)}</td>
									</tr>
								))}
							</tbody>
						</table>
						{!rows.length && (
							<div className="dv-rates__empty">
								No country matches “{query}”.
							</div>
						)}
						<div className="dv-rates__foot">
							Vendor list price + {sheet.marginPct}% margin, rounded up to the
							cent. Vendor: {sheet.vendor}
							{fetched ? ` · catalog updated ${fetched}` : ''}.
						</div>
					</>
				)}
			</aside>
		</div>,
		document.body
	);
};
